// Date range picker for the V2 console.
//
// A PORT of galigali-in/app/brand-manager/shiv/DateRangePicker.js, not a shared
// component, and the distinction is deliberate. That file is a Next.js client
// component styled in Tailwind and importing '@/app/lib/icons'; V2 is a CRA app
// on CSS Modules with lucide-react and its own token set. Importing it would
// mean pulling Tailwind into V2 to style one control.
//
// What IS shared is the behaviour, because that is what an operator has learned:
// the same eleven presets in the same order, the same two-month view, the same
// draft-then-Update commit (a click does not refetch until you say so), the same
// "Maximum" meaning a bounded two-year window rather than an unfiltered view.
// If BM's preset list changes, this should change with it.
//
// It replaces a 7d/30d/90d segmented control that was not merely limited — it
// read as broken. There are Rs 33,497 of revenue in the 31-90 day window and
// ZERO rows from currently-active campaigns, so clicking 90d left the headline
// unmoved and looked like a dead button.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import s from './DateRange.module.css';

const toISO = (d) => {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const parseISO = (v) => {
  if (!v) return null;
  const [y, m, d] = String(v).split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; };
const sameDay = (a, b) => a && b && toISO(a) === toISO(b);
const fmt = (d) => (d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

// BM's list, same order. "Maximum" is a BOUNDED window (Meta semantics), not
// an unfiltered view — a revenue screen with no upper bound on its scan is a
// query nobody intended.
const buildPresets = () => {
  const today = startOfDay(new Date());
  return [
    { key: 'today', label: 'Today', range: () => ({ start: today, end: today }) },
    { key: 'yesterday', label: 'Yesterday', range: () => ({ start: addDays(today, -1), end: addDays(today, -1) }) },
    { key: 'last7', label: 'Last 7 days', range: () => ({ start: addDays(today, -6), end: today }) },
    { key: 'last14', label: 'Last 14 days', range: () => ({ start: addDays(today, -13), end: today }) },
    { key: 'last28', label: 'Last 28 days', range: () => ({ start: addDays(today, -27), end: today }) },
    { key: 'last30', label: 'Last 30 days', range: () => ({ start: addDays(today, -29), end: today }) },
    { key: 'thisWeek', label: 'This week', range: () => ({ start: startOfWeek(today), end: today }) },
    { key: 'lastWeek', label: 'Last week', range: () => { const a = addDays(startOfWeek(today), -7); return { start: a, end: addDays(a, 6) }; } },
    { key: 'thisMonth', label: 'This month', range: () => ({ start: new Date(today.getFullYear(), today.getMonth(), 1), end: today }) },
    { key: 'lastMonth', label: 'Last month', range: () => ({ start: new Date(today.getFullYear(), today.getMonth() - 1, 1), end: new Date(today.getFullYear(), today.getMonth(), 0) }) },
    // Bounded at 400 days, matching the route's own MAX_DAYS clamp — offering a
    // range the server will silently truncate is a lie told by a control.
    { key: 'maximum', label: 'Maximum', range: () => ({ start: addDays(today, -399), end: today }) },
  ];
};

const monthCells = (year, month) => {
  const lead = new Date(year, month, 1).getDay();
  const n = new Date(year, month + 1, 0).getDate();
  const cells = Array(lead).fill(null);
  for (let d = 1; d <= n; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7) cells.push(null);
  return cells;
};

export default function DateRange({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const presets = useMemo(buildPresets, []);
  const today = useMemo(() => startOfDay(new Date()), []);

  const cFrom = useMemo(() => parseISO(from), [from]);
  const cTo = useMemo(() => parseISO(to), [to]);

  const [dStart, setDStart] = useState(cFrom);
  const [dEnd, setDEnd] = useState(cTo);
  const [hover, setHover] = useState(null);
  const [view, setView] = useState(cFrom || today);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const openPicker = () => {
    setDStart(cFrom); setDEnd(cTo); setView(cFrom || today); setOpen(true);
  };

  // First click sets the start and clears the end; a click before the start
  // restarts the range rather than producing an inverted one.
  const clickDay = (day) => {
    if (!dStart || (dStart && dEnd) || day < dStart) { setDStart(day); setDEnd(null); }
    else setDEnd(day);
  };

  const activePreset = useMemo(() => {
    const a = dStart ? toISO(dStart) : '';
    const b = dEnd ? toISO(dEnd) : '';
    return presets.find((p) => {
      const r = p.range();
      return toISO(r.start) === a && toISO(r.end) === b;
    })?.key || null;
  }, [dStart, dEnd, presets]);

  const commit = () => {
    if (!dStart) return;
    onChange(toISO(dStart), toISO(dEnd || dStart));
    setOpen(false);
  };

  const label = () => {
    if (!cFrom || !cTo) return 'Select dates';
    const p = presets.find((x) => {
      const r = x.range();
      return toISO(r.start) === toISO(cFrom) && toISO(r.end) === toISO(cTo);
    });
    if (p) return p.label;
    return sameDay(cFrom, cTo) ? fmt(cFrom) : `${fmt(cFrom)} – ${fmt(cTo)}`;
  };

  const renderMonth = (base) => {
    const y = base.getFullYear();
    const m = base.getMonth();
    const rangeEnd = dEnd || (dStart && hover && hover > dStart ? hover : null);
    return (
      <div className={s.month}>
        <div className={s.monthName}>{MONTHS[m]} {y}</div>
        <div className={s.grid}>
          {WEEKDAYS.map((w) => <div key={w} className={s.weekday}>{w}</div>)}
        </div>
        <div className={s.grid} onMouseLeave={() => setHover(null)}>
          {monthCells(y, m).map((day, i) => {
            if (!day) return <div key={i} className={s.cell} />;
            const isStart = sameDay(day, dStart);
            const isEnd = sameDay(day, rangeEnd);
            const inRange = dStart && rangeEnd && day > dStart && day < rangeEnd;
            const future = day > today;
            return (
              <div key={i} className={`${s.cell} ${inRange ? s.inRange : ''} ${isStart ? s.rangeStart : ''} ${isEnd ? s.rangeEnd : ''}`}>
                <button type="button" disabled={future}
                        className={`${s.day} ${isStart || isEnd ? s.dayOn : ''} ${future ? s.dayOff : ''}`}
                        onMouseEnter={() => setHover(day)}
                        onClick={() => clickDay(day)}>
                  {day.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={s.wrap} ref={wrap}>
      <button type="button" className={`${s.trigger} ${open ? s.triggerOpen : ''}`}
              onClick={() => (open ? setOpen(false) : openPicker())} aria-expanded={open}>
        <Calendar size={13} aria-hidden="true" />
        <span>{label()}</span>
        <ChevronDown size={13} aria-hidden="true" />
      </button>

      {open && (
        <div className={s.panel}>
          <div className={s.presets}>
            {presets.map((p) => (
              <button key={p.key} type="button"
                      className={`${s.preset} ${activePreset === p.key ? s.presetOn : ''}`}
                      onClick={() => { const r = p.range(); setDStart(r.start); setDEnd(r.end); setView(r.start); }}>
                {p.label}
              </button>
            ))}
          </div>

          <div className={s.cal}>
            <div className={s.calHead}>
              <button type="button" className={s.nav} aria-label="Previous month"
                      onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}>
                <ChevronLeft size={15} />
              </button>
              <button type="button" className={s.nav} aria-label="Next month"
                      onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}>
                <ChevronRight size={15} />
              </button>
            </div>
            <div className={s.months}>
              {renderMonth(view)}
              {renderMonth(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
            </div>

            {/* Draft, not applied. Nothing refetches until Update — a revenue
                query is not something to fire on every calendar click. */}
            <div className={s.foot}>
              <span className={s.draft}>
                {dStart ? fmt(dStart) : '—'}
                {' – '}
                {dEnd ? fmt(dEnd) : <em className={s.pending}>pick an end date</em>}
                {dStart && dEnd && (
                  <span className={s.span}>
                    {' · '}{Math.round((dEnd - dStart) / 86400000) + 1} days
                  </span>
                )}
              </span>
              <button type="button" className={s.cancel} onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className={s.apply} disabled={!dStart} onClick={commit}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { toISO, addDays, startOfDay };
